"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useBroadcastChannel } from "@/hooks/use-broadcast-channel";
import { useChatSwitch } from "@/hooks/use-chat-switch";
import { useProfileMenuDisplayName } from "@/hooks/use-profile-menu-display-name";

function getInitials(name: string): string {
  const parts = name.split(" ");
  if (parts.length === 0) {
    return "";
  }
  return parts[0][0] + (parts.length > 1 ? parts[1][0] : "");
}

export function UserNav() {
  const { displayName, user, orgId } = useProfileMenuDisplayName();
  const router = useRouter();
  const { broadcastSignOut } = useBroadcastChannel();
  const { selectedView } = useChatSwitch();

  const handleSignOut = (): void => {
    broadcastSignOut("user-initiated");
    setTimeout(() => {
      router.push("/auth/sign-out");
    }, 50);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative size-8 rounded-full">
          {user ? (
            <Avatar className="size-8">
              <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
            </Avatar>
          ) : (
            <div className="flex items-center justify-center">
              <Skeleton className="size-8 rounded-full" />
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {orgId && (
            <DropdownMenuItem asChild>
              <Link
                href={`/${orgId}/my-account?view=${selectedView}`}
                className="cursor-pointer"
              >
                My Account
                <DropdownMenuShortcut>⌘,</DropdownMenuShortcut>
              </Link>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuItem className="cursor-pointer" onClick={handleSignOut}>
          Sign out
          <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
